import { useMemo } from "react";
import { useSelector } from "react-redux";
import { createSelector } from "reselect";

import { Permission, type QueryView } from "~/models/dto";
import { identity } from "~/tools/identity";
import { prop } from "~/tools/prop";

import type { NodeMap } from "../app/state/global/GlobalState";
import type { RootState } from "../app/state/RootState";

import { usePermissions } from "./use-permissions";
import { useUserProfile } from "./use-user-profile";

const selectQueryViews = createSelector((root: RootState) => root.global.queryViews, identity);

/**
 * Hook to access query views from the global store, filtered by permissions and user profile settings.
 *
 * @return a map of query views, or undefined if the query views are not yet loaded
 */
export function useQueryViews(): undefined | NodeMap<QueryView>;

/**
 * Hook to access query views from the global store, filtered by permissions and user profile settings, with a selector to transform the resulting views.
 *
 * @param select optional selector to transform the resulting views
 *
 * @return the selected result of the selector function, or undefined if the query views are not yet loaded
 */
export function useQueryViews<TResult>(select: (views: NodeMap<QueryView>) => TResult): undefined | TResult;

export function useQueryViews<TResult>(
	select: (views: NodeMap<QueryView>) => TResult = identity as typeof select,
): undefined | TResult {
	const permissions = usePermissions();
	const allQueryViews = useSelector(selectQueryViews);
	const [hideUnavailableConnections] = useUserProfile({
		select: prop("hideUnavailableConnections"),
	});

	/**
	 * Memoize the views before passing so {@link select} receives a stable reference.
	 */
	const normalizedViews = useMemo(() => {
		if (permissions == null || hideUnavailableConnections == null) {
			return undefined;
		}

		return (
			allQueryViews
				// filter out views without permissions
				.filter(({ name }) => permissions.hasResourcePermission(name, Permission.GetViews))
				// hide unavailable connections if the setting is enabled in user profile
				.mapValues((view) => (hideUnavailableConnections ? { ...view, connectedComponents: false } : view))
		);
	}, [allQueryViews, hideUnavailableConnections, permissions]);

	return useMemo(
		() => (normalizedViews == null ? normalizedViews : select(normalizedViews)),
		[select, normalizedViews],
	);
}
